import { supabase } from '@/lib/supabase'
import type { Database } from '@/types/database'

// Types
type EventoParticipante = Database['public']['Tables']['evento_participantes']['Row']
type EventoParticipanteInsert = Database['public']['Tables']['evento_participantes']['Insert']
type ParticipantePagamento = Database['public']['Tables']['participante_pagamentos']['Row']
type ParticipanteCheckin = Database['public']['Tables']['participante_checkins']['Row']
type ParticipanteResposta = Database['public']['Tables']['participante_respostas']['Row']

type SupabaseRelation<T> = T | T[] | null
const toArray = <T>(value: SupabaseRelation<T> | undefined): T[] => {
  if (!value) return []
  return Array.isArray(value) ? value : [value]
}

export interface ParticipanteComRelacoes extends EventoParticipante {
  usuario?: {
    id: string
    nome: string
    email: string
  } | null
  pagamento?: SupabaseRelation<ParticipantePagamento>
  checkin?: SupabaseRelation<ParticipanteCheckin>
  respostas?: ParticipanteResposta[]
}

// Listar participantes de um evento
export async function listarParticipantes(eventoId: string) {
  if (!supabase) throw new Error('Supabase client not initialized')

  const { data, error } = await supabase
    .from('evento_participantes')
    .select(`
      *,
      usuario:usuarios(id, nome, email),
      pagamento:participante_pagamentos(*),
      parcelas:participante_parcelas(*),
      checkin:participante_checkins(*)
    `)
    .eq('evento_id', eventoId)
    .order('criado_em', { ascending: false })

  if (error) throw error
  return data as ParticipanteComRelacoes[]
}

// Buscar participante por ID
export async function buscarParticipante(id: string) {
  if (!supabase) throw new Error('Supabase client not initialized')

  const { data, error } = await supabase
    .from('evento_participantes')
    .select(`
      *,
      usuario:usuarios(id, nome, email, telefone),
      pagamento:participante_pagamentos(*),
      parcelas:participante_parcelas(*),
      checkin:participante_checkins(*),
      respostas:participante_respostas(
        *,
        pergunta:evento_perguntas(*)
      )
    `)
    .eq('id', id)
    .single()

  if (error) throw error
  return data as ParticipanteComRelacoes
}

// Criar participante (inscrição)
export async function criarParticipante(participante: EventoParticipanteInsert) {
  if (!supabase) throw new Error('Supabase client not initialized')

  const { data, error } = await supabase
    .from('evento_participantes')
    .insert(participante)
    .select()
    .single()

  if (error) throw error
  return data as EventoParticipante
}

// Atualizar participante
export async function atualizarParticipante(
  id: string,
  dados: Partial<EventoParticipanteInsert>
) {
  if (!supabase) throw new Error('Supabase client not initialized')

  const { data, error } = await supabase
    .from('evento_participantes')
    .update(dados)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data as EventoParticipante
}

// Deletar participante
export async function deletarParticipante(id: string) {
  if (!supabase) throw new Error('Supabase client not initialized')

  const { error } = await supabase.from('evento_participantes').delete().eq('id', id)

  if (error) throw error
}

// Atualizar status do participante
export async function atualizarStatusParticipante(
  id: string,
  status: 'confirmado' | 'pendente' | 'cancelado'
) {
  if (!supabase) throw new Error('Supabase client not initialized')

  const { data, error } = await supabase
    .from('evento_participantes')
    .update({ status })
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data as EventoParticipante
}

// Confirmar pagamento
export async function confirmarPagamento(params: {
  participanteId: string
  metodo: 'pix' | 'dinheiro' | 'cartao'
  valor: number
  registradoPor: string
}) {
  if (!supabase) throw new Error('Supabase client not initialized')

  // Buscar o pagamento existente
  const { data: pagamentoExistente } = await supabase
    .from('participante_pagamentos')
    .select('id')
    .eq('participante_id', params.participanteId)
    .single()

  if (!pagamentoExistente) {
    throw new Error('Pagamento não encontrado')
  }

  // Atualizar pagamento
  const { data, error } = await supabase
    .from('participante_pagamentos')
    .update({
      status: 'pago',
      metodo: params.metodo,
      valor: params.valor,
      pago_em: new Date().toISOString(),
      registrado_por: params.registradoPor,
    })
    .eq('id', pagamentoExistente.id)
    .select()
    .single()

  if (error) throw error
  return data as ParticipantePagamento
}

// Marcar como isento
export async function marcarComoIsento(participanteId: string, registradoPor: string) {
  if (!supabase) throw new Error('Supabase client not initialized')

  const { data: pagamentoExistente } = await supabase
    .from('participante_pagamentos')
    .select('id')
    .eq('participante_id', participanteId)
    .single()

  if (!pagamentoExistente) {
    throw new Error('Pagamento não encontrado')
  }

  const { data, error } = await supabase
    .from('participante_pagamentos')
    .update({
      status: 'isento',
      registrado_por: registradoPor,
    })
    .eq('id', pagamentoExistente.id)
    .select()
    .single()

  if (error) throw error
  return data as ParticipantePagamento
}

// Registrar check-in
export async function registrarCheckin(participanteId: string, registradoPor: string) {
  if (!supabase) throw new Error('Supabase client not initialized')

  const { data: checkinExistente } = await supabase
    .from('participante_checkins')
    .select('id')
    .eq('participante_id', participanteId)
    .single()

  if (!checkinExistente) {
    throw new Error('Check-in não encontrado')
  }

  const { data, error } = await supabase
    .from('participante_checkins')
    .update({
      status: 'realizado',
      horario: new Date().toISOString(),
      registrado_por: registradoPor,
    })
    .eq('id', checkinExistente.id)
    .select()
    .single()

  if (error) throw error
  return data as ParticipanteCheckin
}

// Desfazer check-in
export async function desfazerCheckin(participanteId: string) {
  if (!supabase) throw new Error('Supabase client not initialized')

  const { data: checkinExistente } = await supabase
    .from('participante_checkins')
    .select('id')
    .eq('participante_id', participanteId)
    .single()

  if (!checkinExistente) {
    throw new Error('Check-in não encontrado')
  }

  const { data, error } = await supabase
    .from('participante_checkins')
    .update({
      status: 'pendente',
      horario: null,
      registrado_por: null,
    })
    .eq('id', checkinExistente.id)
    .select()
    .single()

  if (error) throw error
  return data as ParticipanteCheckin
}

// Salvar respostas do participante
export async function salvarRespostas(
  participanteId: string,
  respostas: Array<{
    pergunta_id: string
    resposta: string
  }>
) {
  if (!supabase) throw new Error('Supabase client not initialized')

  // Deletar respostas antigas
  await supabase.from('participante_respostas').delete().eq('participante_id', participanteId)

  // Inserir novas respostas
  if (respostas.length > 0) {
    const { data, error } = await supabase
      .from('participante_respostas')
      .insert(
        respostas.map((r) => ({
          participante_id: participanteId,
          pergunta_id: r.pergunta_id,
          resposta: r.resposta,
        }))
      )
      .select()

    if (error) throw error
    return data
  }

  return []
}

// Upload de comprovante
export async function uploadComprovante(
  file: File,
  igrejaId: string,
  eventoId: string,
  participanteId: string
) {
  if (!supabase) throw new Error('Supabase client not initialized')

  const fileExt = file.name.split('.').pop()
  const fileName = `${participanteId}-${Date.now()}.${fileExt}`
  const filePath = `${igrejaId}/${eventoId}/${participanteId}/${fileName}`

  const { error: uploadError } = await supabase.storage
    .from('comprovantes-pagamento')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: true,
    })

  if (uploadError) throw uploadError

  // Retornar apenas o caminho do arquivo (não a URL pública pois o bucket é privado)
  // A URL assinada será gerada quando necessário visualizar
  return filePath
}

// Gerar URL assinada para visualizar comprovante
export async function gerarUrlComprovante(filePath: string): Promise<string> {
  if (!supabase) throw new Error('Supabase client not initialized')

  const { data, error } = await supabase.storage
    .from('comprovantes-pagamento')
    .createSignedUrl(filePath, 3600) // URL válida por 1 hora

  if (error) throw error
  return data.signedUrl
}

// Estatísticas do evento
export async function buscarEstatisticasEvento(eventoId: string) {
  if (!supabase) throw new Error('Supabase client not initialized')

  // Buscar participantes com pagamentos e parcelas
  const { data: participantes, error: participantesError } = await supabase
    .from('evento_participantes')
    .select(`
      *,
      pagamento:participante_pagamentos(*),
      parcelas:participante_parcelas(*),
      checkin:participante_checkins(*)
    `)
    .eq('evento_id', eventoId)

  if (participantesError) throw participantesError

  const confirmados = participantes?.filter((p) => p.status === 'confirmado').length || 0
  const pendentes = participantes?.filter((p) => p.status === 'pendente').length || 0
  const cancelados = participantes?.filter((p) => p.status === 'cancelado').length || 0
  // Total de inscritos válidos (excluindo cancelados)
  const total = confirmados + pendentes

  // Receitas (apenas de participantes não cancelados)
  const participantesAtivos = participantes?.filter((p) => p.status !== 'cancelado') || []

  const receitaPrevista = participantesAtivos.reduce((acc, p) => {
    const pagamentos = toArray(p.pagamento)
    const parcelas = toArray((p as any).parcelas)
    const totalPagamentos = pagamentos.reduce((sum, pag) => sum + (pag.valor || 0), 0)
    const totalParcelas = parcelas.reduce((sum, par) => sum + (Number(par.valor) || 0), 0)
    return acc + totalPagamentos + totalParcelas
  }, 0)

  const receitaRecebida = participantesAtivos.reduce((acc, p) => {
    const pagamentos = toArray(p.pagamento)
    const parcelas = toArray((p as any).parcelas)
    const totalPagamentos = pagamentos.reduce(
      (sum, pag) => sum + (pag.status === 'pago' ? pag.valor : 0),
      0
    )
    const totalParcelas = parcelas.reduce(
      (sum, par) => sum + (par.status === 'pago' ? Number(par.valor) : 0),
      0
    )
    return acc + totalPagamentos + totalParcelas
  }, 0)

  const receitaPendente = receitaPrevista - receitaRecebida

  // Pagamentos por método (apenas de participantes não cancelados)
  const pagamentosPorMetodo: Record<string, { quantidade: number; total: number }> = {}
  participantesAtivos.forEach((p) => {
    const pagamentos = toArray(p.pagamento)
    const parcelas = toArray((p as any).parcelas)

    // Pagamentos normais
    pagamentos.forEach((pag) => {
      if (pag.status === 'pago' && pag.metodo) {
        if (!pagamentosPorMetodo[pag.metodo]) {
          pagamentosPorMetodo[pag.metodo] = { quantidade: 0, total: 0 }
        }
        pagamentosPorMetodo[pag.metodo].quantidade++
        pagamentosPorMetodo[pag.metodo].total += pag.valor
      }
    })

    // Parcelas pagas
    parcelas.forEach((par) => {
      if (par.status === 'pago' && par.metodo) {
        if (!pagamentosPorMetodo[par.metodo]) {
          pagamentosPorMetodo[par.metodo] = { quantidade: 0, total: 0 }
        }
        pagamentosPorMetodo[par.metodo].quantidade++
        pagamentosPorMetodo[par.metodo].total += Number(par.valor)
      }
    })
  })

  // Check-ins
  const checkinsRealizados = participantes?.filter((p) => {
    const checkins = toArray(p.checkin)
    return checkins.some((c) => c.status === 'realizado')
  }).length || 0

  return {
    total,
    confirmados,
    pendentes,
    cancelados,
    receitaPrevista,
    receitaRecebida,
    receitaPendente,
    ocupacao: null, // Calcular depois com capacidade do evento
    taxaConfirmacao: total > 0 ? (confirmados / total) * 100 : 0,
    pagamentosPorMetodo,
    checkinsRealizados,
  }
}
